import { X } from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import { useForm } from 'react-hook-form';
import { useUser } from '../../../../../../../../utils/UserContext';
import useApi from '../../../../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../../../../utils/ScreenViewContext';
import {
  AccentButton,
  NeutralButton,
} from '../../../../../../../../shared/Buttons';
import {
  parseCommaNumber,
  useCommaNumberInput,
} from '../../../../../../../../hooks/useInputFormat';
import { MileageReportRequestUpdate } from '../../../SubmitMileage';
import { getCurrentMonthAndDayName } from '../../../../../../../../utils/DateUtils';
import { useQueryClient } from '@tanstack/react-query';
import {
  CatenaMileageNotice,
  handleMileageConflictResponse,
} from '../../../catenaMileage';

// Define the form data type
interface SubmitMileageFormData {
  monthlyMileage: number;
}

export const SubmitMileageUnitModal = ({
  isOpen,
  onClose,
  selectedAsset,
  catenaConnected,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedAsset: any | null;
  /**
   * Customer-level Catena flag, resolved once in AssetDetails and drilled in.
   * `undefined` = not yet known → keep the form locked (no editable flash);
   * only show the explanation once we affirmatively know they're connected.
   */
  catenaConnected?: boolean;
}) => {
  const { userId, userRecord } = useUser();
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const mileageDisabled = catenaConnected !== false;
  const showCatenaNotice = catenaConnected === true;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SubmitMileageFormData>({
    defaultValues: {
      monthlyMileage: undefined,
    },
  });
  const commaInput = useCommaNumberInput(register);

  // Watch all form fields to check if form is complete
  const monthlyMileage = watch('monthlyMileage');

  const onSubmit = (data: SubmitMileageFormData) => {
    const updates: MileageReportRequestUpdate[] = [
      {
        snapshot_id: selectedAsset?.mileageRequest?.snapshot_id,
        cumulative_odometer: parseCommaNumber(data.monthlyMileage),
        truck_id: selectedAsset?.truck_id,
      },
    ];
    submitMileage(updates);
  };

  const submitMileage = async (updates: MileageReportRequestUpdate[]) => {
    setLoading(true);
    try {
      const response = await callProtectedApi({
        url: `/electronicLoggingDevices/reports/${userRecord?.userType}/${userId}`,
        requestType: 'PATCH',
        body: { updates },
        errorMsg: 'Error occurred while updating mileage report requests.',
        // A rejected reading (below the previous one, or an implausible jump) is only
        // fixable if the driver sees why — the API's 400 says which and by how much.
        preferServerErrorMessage: true,
      });
      if (response?.status === HttpStatusCode.Ok) {
        toast.success('Mileage Successfully Updated');
        queryClient.invalidateQueries({
          queryKey: ['mileageReportRequests'],
        });
        onClose();
      } else {
        handleMileageConflictResponse(response, queryClient);
      }
    } catch (e: any) {
      console.error(
        'Error occurred while updating mileage report requests: ',
        e.response
      );
    } finally {
      setLoading(false);
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="fixed inset-0 z-[999] flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="h-auto max-h-[90%] w-full overflow-x-hidden rounded-[4px] bg-white sm:max-w-[90%] md:max-w-[60%] lg:max-w-[600px]">
              <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  Submit Mileage
                </h2>
                <X
                  className="hover:cursor-pointer hover:opacity-80"
                  onClick={onClose}
                  weight="regular"
                  size={24}
                />
              </div>

              <form
                className="flex h-full w-full flex-col items-center gap-6 overflow-y-auto overflow-x-hidden border-[1px] border-bordergray p-6"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="w-full">
                  {showCatenaNotice && (
                    <div className="mb-6">
                      <CatenaMileageNotice />
                    </div>
                  )}
                  <p className="mb-6 block text-base font-normal text-graysecondary">
                    Enter your truck’s current odometer reading as of{' '}
                    <span className="font-semibold">
                      {getCurrentMonthAndDayName()}
                    </span>
                    .
                  </p>
                  <label className="block text-sm font-medium text-gray-700">
                    Current Odometer Reading
                  </label>
                  <input
                    placeholder={mileageDisabled ? '' : 'Ex. 200,000'}
                    type="text"
                    inputMode="numeric"
                    disabled={mileageDisabled}
                    {...commaInput('monthlyMileage', {
                      required: 'Current Odometer Reading is required',
                      pattern: {
                        value: /^[0-9,]*$/,
                        message:
                          'Invalid Current Odometer Reading, must be digits',
                      },
                    })}
                    id="monthlyMileage"
                    className={`mt-1 block w-full border px-3 py-2 ${errors.monthlyMileage ? 'border-red-500' : 'border-gray-300'} ${mileageDisabled ? 'cursor-not-allowed bg-gray-100 text-gray-500' : 'bg-white'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                  />
                  {errors.monthlyMileage && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.monthlyMileage.message}
                    </p>
                  )}
                </div>
              </form>

              {/* Buttons outside the form */}
              <div className="flex justify-between space-x-2 p-4">
                <NeutralButton
                  onClick={onClose}
                  addClass="rounded-full w-[238px] h-[48px] flex flex-row items-center justify-center"
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  submit={true}
                  onClick={handleSubmit(onSubmit)}
                  disabled={!monthlyMileage || loading || mileageDisabled}
                  addClass="rounded-full hover:bg-yellow-600 w-[238px] h-[48px] disabled:opacity-50 disabled:bg-yellow-600 flex flex-row items-center justify-center"
                >
                  {!loading ? (
                    'Save'
                  ) : (
                    <ClipLoader
                      color="#1D213B"
                      loading={loading}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="h-full w-full justify-center"
                    />
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return <>{renderScreenView(screenView)}</>;
};

export default SubmitMileageUnitModal;
